import { publishCollectionAdded, subscribeBottlesChanged, subscribeCollectionAdded } from '../lib/collectionEvents'
import { formatSizeEntries, indexInventory } from '../lib/bottleFormat'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../App'
import { classifyCollection } from '../lib/collectionStatus'
import { optimisticCollectionRows, persistOwnershipBatch } from '../lib/collectionAcquisition'
import {
  bottleInventoryInsertRows,
  bottleSizesForFragrance,
} from '../lib/bottleInventory'
import {
  applyCollectionFilters,
  collectionFacetOptions,
  complimentCountsByFragrance,
  filterableConcentration,
} from '../lib/collectionFilters'
import { shelfAccordProfile } from '../lib/accordStandouts'

const COLLECTION_DISPLAY_TABLE = 'collection_display'

// ── Specialty helper ──────────────────────────────────────────────────────────
// Pheromones and perfume oils are "specialty" — kept in the total count but
// hidden from the grid by default. Exception: Superz-brand oils stay visible.
export function isSpecialty(g) {
  // Check all concentrations in the group (not just the first one)
  const concs = g.concentrations?.length > 0 ? g.concentrations : [g.concentration || '']
  const name = (g.name || '').toLowerCase()
  const house = (g.house || '').toLowerCase()

  // Pheromone products label themselves in the name OR any concentration
  if (name.includes('pheromone')) return true

  for (const c of concs) {
    const cl = c.toLowerCase()
    if (cl.includes('pheromone')) return true
    // Perfume oils are specialty — except Superz brand oils stay visible
    if (/\boils?\b/.test(cl) && !house.includes('superz') && !house.includes('super z')) return true
  }
  return false
}

export function isFinishedGroup(g) {
  return Boolean(g?.finished)
}

function deprioritizeImageFlagged(fragrances) {
  return fragrances
    .map((fragrance, index) => ({ fragrance, index }))
    .sort((a, b) => {
      const aFlagged = a.fragrance.image_flagged === true
      const bFlagged = b.fragrance.image_flagged === true
      if (aFlagged !== bFlagged) return aFlagged ? 1 : -1
      return a.index - b.index
    })
    .map(({ fragrance }) => fragrance)
}

export function dailyCollectionSeed(date = new Date()) {
  return Number(
    `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`
  )
}

export function seededDailyShuffle(fragrances, seed = dailyCollectionSeed()) {
  const shuffled = fragrances.map((fragrance, index) => ({ fragrance, index }))
  let state = seed || 1
  const next = () => {
    state = (state * 1664525 + 1013904223) >>> 0
    return state
  }

  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = next() % (i + 1)
    const temp = shuffled[i]
    shuffled[i] = shuffled[j]
    shuffled[j] = temp
  }

  return shuffled.map(({ fragrance }) => fragrance)
}

export function isDefaultCollectionBrowse(filters = {}) {
  const hasSeasonRestriction = Array.isArray(filters.seasons) && filters.seasons.length === 1
  return ![
    'search',
    'house',
    'concentrations',
    'accords',
    'leanings',
    'mostComplimented',
    'showSpecialty',
    'showFinished',
    'sort',
  ].some(key => Boolean(filters[key])) && !hasSeasonRestriction
}

// ── Size-grouping helper ──────────────────────────────────────────────────────
// Default: group by (name, house, concentration) — each concentration gets its
// own card (e.g. La Nuit de L'Homme EDT ≠ EDP).
//
// Exception: if two entries share the SAME non-empty fragrantica_url AND the
// same (name, house) they are the same fragrance on one Fragrantica page (same
// notes), so they collapse onto one card.
// e.g. Le Beau EDT + EDP pointing at the same URL → one card with both pills.
//
// IMPORTANT: the URL alone is NOT a safe merge key. Flankers and clones often
// carry the base fragrance's fragrantica_url (e.g. "9PM Rebel" → 9PM's URL,
// Montagne "Ciel Elixir" → Dior Sauvage Elixir's URL). Keying on URL alone
// silently merged ~11 distinct fragrances into another card. Scoping the URL
// merge to matching (name, house) keeps the EDT/EDP merge while preventing
// distinct fragrances from disappearing.
//
// Each card tracks:
//   • concentrations[] — pills when more than one
//   • sizes[]          — { ml, id, concentration, isSample, format?, count? }
//   • members[]        — every collection row on the card, so a grouped card's
//                        second row stays reachable from the detail page
// A group is only marked given_away when EVERY member is given away.
// inventoryByFragrance (bottle rows keyed by fragrance id) lets a row whose bottles
// carry a kind show "Sample · 1.5 ml ×3" instead of its whole-number bottle_ml.
export function groupByScentAndSize(fragrances, complimentCounts = new Map(), inventoryByFragrance = new Map()) {
  const groups = new Map()

  for (const f of fragrances) {
    const url = (f.fragrantica_url || '').trim()
    // Same Fragrantica URL AND same (name, house) → same card (shared notes page,
    // e.g. EDT + EDP of one fragrance). URL is scoped by name+house so flankers /
    // clones that borrow a base fragrance's URL are NOT merged away.
    // No URL → keyed by (name, house, concentration).
    const key = url
      ? `url__${url}__${(f.name || '').toLowerCase().trim()}__${(f.house || '').toLowerCase().trim()}`
      : [
          (f.name          || '').toLowerCase(),
          (f.house         || '').toLowerCase(),
          (f.concentration || '').toLowerCase(),
        ].join('__')

    if (!groups.has(key)) {
      groups.set(key, {
        ...f,
        image_flagged: f.image_flagged === true,
        inventory_count: 0,
        sizes: [],
        concentrations: [],
        resolved_concentrations: [],
        members: [],
        accord_tags: [],
        compliment_count: 0,
        _allGivenAway: true,
        _allFinished: true,
        _anyFinished: false,
      })
    }

    const group = groups.get(key)
    group.members.push({
      id: f.id,
      concentration: f.concentration || null,
      bottle_ml: f.bottle_ml ?? null,
      finished_at: f.finished_at || null,
      given_away: Boolean(f.given_away),
    })
    group.inventory_count += Math.max(1, Number(f.bottle_count) || 1)
    group.compliment_count += Number(complimentCounts.get(f.id) || 0)

    if (f.image_flagged === true) group.image_flagged = true

    // A group is only "all given away" if every member is given away
    if (!f.given_away) group._allGivenAway = false
    // A group is only finished when every bottle row in that grouped card is finished.
    if (f.finished_at) group._anyFinished = true
    else group._allFinished = false

    // Track unique concentrations
    const conc = f.concentration || ''
    if (conc && !group.concentrations.includes(conc)) {
      group.concentrations.push(conc)
    }

    const resolvedConcentration = filterableConcentration(f)
    if (resolvedConcentration && !group.resolved_concentrations.some(value => value.toLocaleLowerCase() === resolvedConcentration.toLocaleLowerCase())) {
      group.resolved_concentrations.push(resolvedConcentration)
    }

    for (const accord of Array.isArray(f.accord_tags) ? f.accord_tags : []) {
      if (accord && !group.accord_tags.some(value => value.toLocaleLowerCase() === String(accord).toLocaleLowerCase())) {
        group.accord_tags.push(accord)
      }
    }

    // Track sizes — mark as sample only if concentration is explicitly "Sample"
    // 10ml travel sizes are NOT samples — they show as "10ml"
    const isRowSample = conc.toLowerCase() === 'sample'
    const formatted = formatSizeEntries(f, inventoryByFragrance.get(f.id) || [])
    if (formatted.length > 0) {
      group.sizes.push(...formatted)
    } else if (f.bottle_ml != null || isRowSample) {
      group.sizes.push({
        ml: f.bottle_ml,
        id: f.id,
        concentration: conc,
        isSample: isRowSample,
        finished_at: f.finished_at || null,
      })
    }
    // WI-532 bottles_v2: every on-hand bottle row, one entry each, for the combined
    // size chip ("100 ml + 10 ml"). Flag OFF never reads this.
    group.bottleSizes = [...(group.bottleSizes || []), ...bottleSizesForFragrance(f, inventoryByFragrance.get(f.id) || [])]
  }

  for (const g of groups.values()) {
    g.given_away = g._allGivenAway
    g.finished = g._allFinished
    g.any_finished = g._anyFinished
    if (g.finished && !g.finished_at) {
      const dates = g.sizes.map(s => s.finished_at).filter(Boolean).sort()
      g.finished_at = dates[dates.length - 1] || null
    }
    delete g._allGivenAway
    delete g._allFinished
    delete g._anyFinished
    // Largest bottle first; nulls (no-ml samples) at end
    g.sizes.sort((a, b) => {
      if (a.ml == null && b.ml == null) return 0
      if (a.ml == null) return 1
      if (b.ml == null) return -1
      return b.ml - a.ml
    })
  }

  return [...groups.values()]
}

// ── Main collection hook ──────────────────────────────────────────────────────
export function useCollection(filters = {}) {
  const { user } = useAuth()
  const [allFragrances, setAllFragrances] = useState([])
  const [wearHistory, setWearHistory] = useState([])
  const [complimentCounts, setComplimentCounts] = useState(new Map())
  const [bottleRows, setBottleRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const addedRows = useRef(new Map())
  useEffect(() => { addedRows.current.clear() }, [user?.id])

  useEffect(() => subscribeCollectionAdded(rows => {
    const owned = rows.filter(row => row.user_id === user?.id)
    if (!owned.length) return
    for (const row of owned) addedRows.current.set(row.id, row)
    const ids = new Set(owned.map(row => row.id))
    setAllFragrances(previous => [...previous.filter(row => !ids.has(row.id)), ...owned])
  }), [user?.id])

  const fetchCollection = useCallback(async () => {
    if (!user) return
    setLoading(true)
    setError(null)
    try {
      // Fetch everything at once (incl. given_away rows so we can group correctly
      // before deciding what to show/count client-side)
      const [collectionResult, wearLogsResult, complimentEventsResult] = await Promise.all([
        supabase
        .from(COLLECTION_DISPLAY_TABLE)
        .select('*')
        .eq('user_id', user.id)
        .order('name', { ascending: true })
        .limit(1000),
        supabase
          .from('wear_logs')
          .select('id,fragrance_id,compliments,worn_on')
          .eq('user_id', user.id),
        supabase
          .from('compliment_events')
          .select('id,wear_log_id')
          .eq('user_id', user.id),
      ])

      if (collectionResult.error) throw collectionResult.error
      const snapshot = collectionResult.data || []
      for (const row of snapshot) addedRows.current.delete(row.id)
      setAllFragrances([...snapshot, ...addedRows.current.values()])
      if (wearLogsResult.error) throw wearLogsResult.error
      if (complimentEventsResult.error) throw complimentEventsResult.error
      setWearHistory(wearLogsResult.data || [])
      setComplimentCounts(complimentCountsByFragrance(
        wearLogsResult.data || [],
        complimentEventsResult.data || [],
      ))
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => { fetchCollection() }, [fetchCollection])

  // Bottle rows are optional decoration for the size chips: if they cannot be read the
  // shelf still renders from collection_display exactly as before.
  const fetchBottleRows = useCallback(async () => {
    if (!user) return
    const { data, error: bottleError } = await supabase
      .from('bottle_inventory')
      .select('id,fragrance_id,size_ml,status,bottle_format,created_at')
      .eq('user_id', user.id)
      .in('status', ['sealed', 'in_use'])
    if (bottleError) {
      console.warn('[collection] bottle rows unavailable', bottleError.message)
      return
    }
    setBottleRows(data || [])
  }, [user])

  useEffect(() => { fetchBottleRows() }, [fetchBottleRows])
  useEffect(() => subscribeBottlesChanged(() => { fetchBottleRows() }), [fetchBottleRows])

  // ── Collection status metrics (P2: deterministic classification) ──────────
  const statusMetrics = useMemo(
    () => classifyCollection(allFragrances),
    [allFragrances]
  )

  // ── Derived display state ────────────────────────────────────────────────────
  const inventoryByFragrance = useMemo(() => indexInventory(bottleRows), [bottleRows])
  const grouped = useMemo(
    () => groupByScentAndSize(allFragrances, complimentCounts, inventoryByFragrance),
    [allFragrances, complimentCounts, inventoryByFragrance],
  )
  const filterOptions = useMemo(() => collectionFacetOptions(grouped.filter(group => !group.given_away)), [grouped])
  // Step 1: given-away bottles are archival and do not participate in the shelf.
  const withGivenAway = useMemo(() => grouped.filter(g => !g.given_away), [grouped])

  // Step 2: hide finished groups unless explicitly shown. Partially finished
  // grouped cards stay active because at least one bottle remains in rotation.
  const withFinished = useMemo(
    () => filters.showFinished ? withGivenAway : withGivenAway.filter(g => !isFinishedGroup(g)),
    [withGivenAway, filters.showFinished],
  )

  // Accord counts across the active shelf, independent of the grid's filters,
  // so a standout chip means "rare among everything you own", not "rare in this search".
  const shelfAccords = useMemo(
    () => shelfAccordProfile(withGivenAway.filter(g => !isFinishedGroup(g))),
    [withGivenAway],
  )

  // Step 3: hide specialty items (oils / pheromones) unless explicitly shown
  // Callers that pass no filters get a fresh `{}` every render; key the memo on
  // the filter VALUES so consumers (e.g. LayeringAdvisorPage effects) see a
  // stable array and do not re-run on every render.
  const filtersKey = JSON.stringify(filters || {})
  const fragrances = useMemo(() => {
    const withoutSpecialty = filters.showSpecialty
      ? deprioritizeImageFlagged(withFinished)
      : deprioritizeImageFlagged(withFinished.filter(g => !isSpecialty(g)))
    return applyCollectionFilters(withoutSpecialty, filters)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withFinished, filtersKey])

  // Count matches exactly what is rendered — the headline can never claim more
  // (or fewer) fragrances than the grid actually shows. Closes the silent
  // header/grid gap (previously the header counted specialty items the grid hid).
  const total = fragrances.length
  const bottleCount = allFragrances.filter(f => !f.deleted_at && !f.given_away && !f.finished_at).length

  // Transparency: distinct fragrances hidden by the DEFAULT filters. They are
  // intentionally hidden but reachable via Filters → "Show Hidden Items", so we
  // surface them as a count instead of letting them vanish silently.
  const hiddenSpecialty = filters.showSpecialty
    ? 0
    : withFinished.filter(g => isSpecialty(g)).length
  const hiddenFinished = filters.showFinished
    ? 0
    : withGivenAway.filter(g => isFinishedGroup(g)).length
  const hiddenCount = hiddenFinished + hiddenSpecialty

  // ── Duplicate detection (soft, client-side) ───────────────────────────────────
  // The collection intentionally allows multiple bottles per scent, so this is a
  // WARNING signal — never a hard block. Matches on name + house + concentration,
  // case-insensitive, across the user's non-deleted rows.
  const findDuplicate = ({ name, house, concentration }) => {
    const n = (name || '').trim().toLowerCase()
    const h = (house || '').trim().toLowerCase()
    const c = (concentration || '').trim().toLowerCase()
    if (!n || !h) return null
    return allFragrances.find(f =>
      !f.deleted_at &&
      (f.name || '').trim().toLowerCase() === n &&
      (f.house || '').trim().toLowerCase() === h &&
      (f.concentration || '').trim().toLowerCase() === c
    ) || null
  }

  // ── Mutations ────────────────────────────────────────────────────────────────
  // Acquisition is optimistic in the client and atomic in PostgreSQL. Every
  // selected fragrance and its primary bottle_inventory row either commit as
  // one batch or all roll back; no compensating delete is needed.
  const addFragrances = async (items) => {
    if (!user) throw new Error('Not authenticated')
    const optimistic = optimisticCollectionRows(items, user.id)
    const optimisticIds = new Set(optimistic.map(item => item.id))
    setAllFragrances(prev => [...prev, ...optimistic])
    try {
      const persisted = await persistOwnershipBatch(supabase, items)
      setAllFragrances(prev => [
        ...prev.filter(item => !optimisticIds.has(item.id)),
        ...persisted,
      ])
      publishCollectionAdded(persisted)
      return persisted
    } catch (mutationError) {
      setAllFragrances(prev => prev.filter(item => !optimisticIds.has(item.id)))
      throw mutationError
    }
  }

  const addFragrance = async (data, acquisition = null) => {
    const [created] = await addFragrances([{
      ...data,
      bottle_ml: acquisition?.bottle_ml ?? data.bottle_ml ?? null,
    }])
    return created
  }

  const addBackupBottle = async (id, details = {}) => {
    const rows = bottleInventoryInsertRows({
      userId: user.id,
      fragranceId: id,
      details,
      count: details.count ?? 1,
    })
    const { data: inserted, error } = await supabase
      .from('bottle_inventory')
      .insert(rows)
      .select('id,fragrance_id,size_ml,status,finished_at')
    if (error) throw error
    if (inserted?.length) fetchBottleRows()
    const displayRow = await fetchDisplayFragrance(id, user.id)
    setAllFragrances(prev => prev.map(fragrance => fragrance.id === id ? displayRow : fragrance))
    return displayRow
  }

  const updateFragrance = async (id, updates) => {
    const { data: updated, error } = await supabase
      .from('fragrances').update(updates).eq('id', id).eq('user_id', user.id).select().single()
    if (error) throw error
    const displayRow = await fetchDisplayFragrance(updated.id, user.id)
    setAllFragrances(prev => prev.map(f => f.id === id ? displayRow : f))
    return displayRow
  }

  // Soft-delete
  const deleteFragrance = async (id) => {
    const { error } = await supabase
      .from('fragrances')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', user.id)
    if (error) throw error
    setAllFragrances(prev => prev.filter(f => f.id !== id))
  }

  return {
    wearHistory,
    allFragrances,
    fragrances,
    shelfAccords,
    total,
    bottleCount,
    hiddenCount,
    loading,
    error,
    statusMetrics,
    filterOptions,
    addFragrance,
    addFragrances,
    addBackupBottle,
    findDuplicate,
    updateFragrance,
    deleteFragrance,
    refresh: fetchCollection,
  }
}

// ── Single-fragrance hook (detail page) ──────────────────────────────────────
export function useFragrance(id) {
  const { user } = useAuth()
  const [fragrance, setFragrance] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!id || !user) return
    supabase.from(COLLECTION_DISPLAY_TABLE).select('*').eq('id', id).eq('user_id', user.id).single()
      .then(({ data, error }) => {
        if (!error) setFragrance(data)
        setLoading(false)
      })
  }, [id, user])

  return { fragrance, loading, setFragrance }
}

async function fetchDisplayFragrance(id, userId) {
  const { data, error } = await supabase
    .from(COLLECTION_DISPLAY_TABLE)
    .select('*')
    .eq('id', id)
    .eq('user_id', userId)
    .single()
  if (error) throw error
  return data
}
